import React, { useEffect, useState } from "react";
import { Card } from "reactstrap";
import Modal from "react-bootstrap/Modal";
import { Button } from "react-bootstrap";
import { SearchIcon } from "lucide-react";
import SearchFilterButtons from "../SearchFilterButtons";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import CommonTable from "../CommonTable";
import AddEquipment from "./AddEquipment";
import { useLoader } from "../../_helper/LoaderContext";
import {
    deleteEquipment,
    EQUIPMENT_CATEGORIES,
    getEquipmentList,
    toggleEquipmentStatus,
    viewEquipment,
} from "./service/equipment-service";

export default function EquipmentHome() {
    const { t } = useTranslation();
    const { setLoading } = useLoader();
    const [show, setShow] = useState(false);
    const [edit, setEdit] = useState(false);
    const [view, setView] = useState(false);
    const [data, setData] = useState({});
    const [viewData, setViewData] = useState(null);
    const [equipment, setEquipment] = useState([]);
    const [search, setSearch] = useState("");
    const [appliedSearch, setAppliedSearch] = useState("");
    const [category, setCategory] = useState("");
    const [appliedCategory, setAppliedCategory] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [totalEntries, setTotalEntries] = useState(0);

    const columns = [
        { header: t("Name"), accessor: "name", width: "30%" },
        { header: t("Code"), accessor: "code", width: "20%" },
        {
            header: t("Category"),
            accessor: "category",
            width: "25%",
            render: (value) =>
                Array.isArray(value) ? value.join(", ") : value || "-",
        },
    ];

    const getList = async (query = appliedSearch, selectedCategory = appliedCategory) => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("search", query);
            formData.append("page", currentPage);
            formData.append("limit", entriesPerPage);
            if (selectedCategory) {
                formData.append("category", selectedCategory);
            }
            const res = await getEquipmentList(formData);
            if (res.data.success === true) {
                const payload = res.data.data;
                setEquipment(payload?.equipments || []);
                setTotalEntries(payload?.totalCount || 0);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getList();
    }, [currentPage, entriesPerPage]);

    const handleSearch = () => {
        const query = search.trim();
        setAppliedSearch(query);
        setAppliedCategory(category);
        if (currentPage === 1) {
            getList(query, category);
        } else {
            setCurrentPage(1);
        }
    };

    const handleClear = () => {
        setSearch("");
        setCategory("");
        const hadFilter = Boolean(appliedSearch || appliedCategory);
        setAppliedSearch("");
        setAppliedCategory("");
        if (!hadFilter) return;
        if (currentPage === 1) {
            getList("", "");
        } else {
            setCurrentPage(1);
        }
    };

    const totalPages = Math.ceil(totalEntries / entriesPerPage) || 1;

    const handleToggleStatus = (row) => {
        Swal.fire({
            title: t("Are you sure?"),
            text: row.isActive
                ? t("You want to inactivate this equipment?")
                : t("You want to activate this equipment?"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: row.isActive === true ? t("Inactive") : t("Active"),
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoading(true);
                try {
                    const res = await toggleEquipmentStatus(row._id);
                    if (res.data.success === true) {
                        toast.success(res.data.message);
                        getList();
                    }
                } catch (error) {
                    console.log(error);
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleView = async (row) => {
        setLoading(true);
        try {
            const res = await viewEquipment(row._id);
            if (res.data.success === true) {
                setViewData(res.data.data || row);
                setView(true);
            } else {
                setViewData(row);
                setView(true);
            }
        } catch (error) {
            console.log(error);
            setViewData(row);
            setView(true);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (row) => {
        setData(row);
        setEdit(true);
    };

    const handleDelete = (row) => {
        Swal.fire({
            title: t("Are you sure?"),
            text: t("You want to delete this equipment?"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: t("Yes, delete it!"),
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoading(true);
                try {
                    const res = await deleteEquipment(row._id);
                    if (res.data.success === true) {
                        toast.success(res.data.message);
                        getList();
                    }
                } catch (error) {
                    console.log(error);
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const actions = [
        { label: t("View"), onClick: (row) => handleView(row) },
        { label: t("Edit"), onClick: (row) => handleEdit(row) },
        { label: t("Delete"), onClick: (row) => handleDelete(row) },
    ];

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    const editClose = () => setEdit(false);
    const viewClose = () => {
        setView(false);
        setViewData(null);
    };

    return (
        <>
            <Card>
                <div className="px-3 mt-4">
                    <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
                        <div className="input-group" style={{ flex: "1 1 240px", maxWidth: 380 }}>
                            <span className="input-group-text tw-bg-white" id="equipment-search">
                                <SearchIcon />
                            </span>
                            <input
                                type="text"
                                className="form-control icon_form"
                                placeholder={t("Search by name and code")}
                                aria-label={t("Search by name and code")}
                                aria-describedby="equipment-search"
                                style={{ borderLeft: "none" }}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") handleSearch();
                                }}
                            />
                        </div>
                        <select
                            className="form-control"
                            aria-label={t("Filter by category")}
                            style={{ flex: "0 1 220px", maxWidth: 220, height: 38 }}
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                        >
                            <option value="">{t("All Categories")}</option>
                            {EQUIPMENT_CATEGORIES.map((cat) => (
                                <option key={cat} value={cat}>
                                    {cat}
                                </option>
                            ))}
                        </select>
                        <SearchFilterButtons onSearch={handleSearch} onClear={handleClear} />
                        <div className="ms-md-auto">
                            <button className="btn btn-primary" onClick={handleShow}>
                                {t("Add")}
                            </button>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <CommonTable
                                columns={columns}
                                data={equipment}
                                onToggleStatus={handleToggleStatus}
                                onAction={actions}
                                totalPages={totalPages}
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                entriesPerPage={entriesPerPage}
                                setEntriesPerPage={setEntriesPerPage}
                            />
                        </div>
                    </div>
                </div>
            </Card>

            <Modal show={show} onHide={handleClose} centered size="md">
                <Modal.Header closeButton>
                    <Modal.Title>{t("Add Equipment")}</Modal.Title>
                </Modal.Header>
                <AddEquipment key={show ? "add-open" : "add-closed"} handleClose={handleClose} getList={getList} />
            </Modal>

            <Modal show={edit} onHide={editClose} centered size="md">
                <Modal.Header closeButton>
                    <Modal.Title>{t("Edit Equipment")}</Modal.Title>
                </Modal.Header>
                <AddEquipment handleClose={editClose} data={data} getList={getList} />
            </Modal>

            <Modal show={view} onHide={viewClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t("Equipment Details")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {viewData && (
                        <div className="row g-3">
                            <div className="col-md-6">
                                <small className="text-muted d-block">{t("Name")}</small>
                                <strong>{viewData.name || "-"}</strong>
                            </div>
                            <div className="col-md-6">
                                <small className="text-muted d-block">{t("Code")}</small>
                                <strong>{viewData.code || "-"}</strong>
                            </div>
                            <div className="col-md-6">
                                <small className="text-muted d-block">{t("Category")}</small>
                                <strong>
                                    {Array.isArray(viewData.category)
                                        ? viewData.category.join(", ")
                                        : viewData.category || "-"}
                                </strong>
                            </div>
                            <div className="col-md-6">
                                <small className="text-muted d-block">{t("Status")}</small>
                                <strong>
                                    {viewData.isActive === true ? t("Active") : t("Inactive")}
                                </strong>
                            </div>
                            <div className="col-12">
                                <small className="text-muted d-block">{t("Description")}</small>
                                <p className="mb-0">{viewData.description || "-"}</p>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={viewClose}>
                        {t("Close")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
